import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { fmt } from "../../data/site";

const ago = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return new Date(iso).toLocaleDateString();
};

const Stage = ({ basket, paidIds }) => {
  const testid = `basket-stage-${basket.session_id.slice(0, 8)}`;
  if (basket.order_id && paidIds.has(basket.order_id))
    return <span className="rounded-full bg-halal/10 px-2.5 py-1 font-mono text-[10px] font-bold text-halal" data-testid={testid}>Paid</span>;
  if (basket.checkout_started)
    return <span className="rounded-full bg-saffron/15 px-2.5 py-1 font-mono text-[10px] font-semibold text-ink/80" data-testid={testid}>Reached checkout</span>;
  return <span className="rounded-full bg-ink/5 px-2.5 py-1 font-mono text-[10px] font-semibold text-muted2" data-testid={testid}>Browsing</span>;
};

/** Live view of visitor baskets from the Meal Prep page — the top of the order funnel. */
export const BasketsPanel = ({ orders }) => {
  const [baskets, setBaskets] = useState(null);
  const paidIds = new Set(orders.filter((o) => o.payment_status === "paid").map((o) => o.id));

  useEffect(() => {
    api.get("/admin/baskets").then(({ data }) => setBaskets(data)).catch((err) => toast.error(formatApiError(err)));
  }, []);

  const emailFor = (b) => b.email || orders.find((o) => o.id === b.order_id)?.email;

  return (
    <div className="overflow-x-auto rounded-2xl border border-inkline bg-cream" data-testid="baskets-panel">
      <div className="border-b border-inkline px-4 py-3 text-xs text-muted2">
        Every bundle a visitor starts building shows up here. You get an email when a basket is started, when they reach checkout (with their email), when they pay, and if they abandon it.
      </div>
      <table className="w-full min-w-[720px] text-left text-sm" data-testid="baskets-table">
        <thead>
          <tr className="border-b border-inkline font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">
            <th className="p-4">Last activity</th><th className="p-4">Items</th><th className="p-4">Meals</th><th className="p-4">Value</th><th className="p-4">Customer</th><th className="p-4">Stage</th>
          </tr>
        </thead>
        <tbody>
          {baskets === null && <tr><td colSpan={6} className="p-8 text-center text-muted2">Loading…</td></tr>}
          {baskets?.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted2">No baskets yet — they appear as soon as a visitor adds a meal.</td></tr>}
          {baskets?.map((b) => (
            <tr key={b.session_id} className="border-b border-inkline/60 last:border-0" data-testid={`basket-row-${b.session_id.slice(0, 8)}`}>
              <td className="p-4 text-xs text-muted2">{ago(b.updated_at)}</td>
              <td className="p-4 text-xs text-ink/80">{b.items.map((i) => `${i.qty}× ${i.name}`).join(", ") || "—"}</td>
              <td className="p-4 font-mono text-xs">{b.meal_count}</td>
              <td className="p-4 font-mono text-xs font-bold">{fmt(b.food_total || 0)}</td>
              <td className="p-4 text-xs text-ink/80">{emailFor(b) || <span className="text-muted2">not yet</span>}</td>
              <td className="p-4"><Stage basket={b} paidIds={paidIds} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
